import React, { useState } from 'react';
import { Box, DialogContent, Button, Typography, Tab, Tabs, Tooltip, CircularProgress, Divider, Chip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import { IPushbackReport } from '../../models/configuration/pushback/IPushbackReport';
import dayjs from 'dayjs';
import { pushBackDetailColumnReport } from './pushbackData';
import { getPushbackStatusColor } from '../../utils/statusColors';
import { clientLogs } from '../clientData';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { DtDataGrid } from '../../components/ui/dataGrid';
import { DtDialog } from '../../components/ui/dialog';

interface PushbackReportDialogProps {
    open: boolean;
    closeFxtn: () => void;
    reportData: IPushbackReport | null;
    loading?: boolean;
    onRefresh?: () => void;
}

const PushbackReportingDialog: React.FC<PushbackReportDialogProps> = ({ open, closeFxtn, reportData, loading = false, onRefresh }) => {
    const [tabValue, setTabValue] = useState(0);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const closeDialog = () => {
        closeFxtn();
    };

    return (
        <DtDialog
            actions={<Button onClick={closeDialog} variant="contained" size='small' color="primary">Close</Button>}
            title={
                <Box sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Pushback Report {reportData?.Report?.PushbackEntry.OrderId && `[${reportData?.Report?.PushbackEntry.OrderId}]`}
                    </Typography>

                </Box>}
            open={open}
            onClose={closeDialog}
            fullWidth
            maxWidth="lg">

            {loading ? (
                <DialogContent sx={{ position: 'relative', padding: 0 }}>
                    <Box sx={{ position: 'absolute', right: 0}}>
                        <Button
                            aria-label="refresh"
                            onClick={onRefresh}
                            size="small"
                            startIcon={<RefreshIcon fontSize="small" />}
                            sx={{ ml: 2 }}
                            color="primary"
                            disabled={loading}
                        >
                            Refresh
                        </Button>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: 400,
                            flexDirection: 'column',
                            gap: 2
                        }}
                    >
                        <CircularProgress size={40} />
                        <Typography variant="body1" color="textSecondary">
                            Loading pushback report...
                        </Typography>
                    </Box>
                </DialogContent>
            ) : (
                <>
                    <DialogContent sx={{ position: 'relative', padding: 0 }}>

                        <Box
                        >
                            <Box sx={{ position: 'absolute', right: 0 }}>
                                <Button
                                    aria-label="refresh"
                                    onClick={onRefresh}
                                    size="small"
                                    startIcon={<RefreshIcon fontSize="small" />}
                                    sx={{ ml: 2 }}
                                    color="primary"
                                    disabled={loading}
                                >
                                    Refresh
                                </Button>
                            </Box>
                            {/* Push Back Details */}
                            <Box sx={{ width: '100%', overflowX: 'hidden', py: 1 }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                                    {/* Left Column */}
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 1.5 }}>
                                        <Typography variant="body1" color="textSecondary"><strong>Loan Number:</strong></Typography>
                                        <Typography variant="body1">{reportData?.Report?.PushbackEntry.LoanNumber}</Typography>
                                        <Typography variant="body1" color="textSecondary"><strong>Reason:</strong></Typography>
                                        <Typography variant="body1">{reportData?.Report?.PushbackEntry.Reason}</Typography>
                                        <Typography variant="body1" color="textSecondary"><strong>Package Type:</strong></Typography>
                                        <Typography variant="body1">{reportData?.Report?.PushbackEntry.PackageType}</Typography>
                                    </Box>
                                    {/* Right Column */}
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 1.5 }}>
                                        <Typography variant="body1" color="textSecondary"><strong>Pushback Received:</strong></Typography>
                                        <Typography variant="body1">{dayjs(reportData?.Report?.PushbackEntry.ReceivedTimestamp).format('MM/DD/YYYY hh:mm:ss A Z')}</Typography>
                                        <Typography variant="body1" color="textSecondary"><strong>Status:</strong></Typography>
                                        <Typography
                                            variant="body1"
                                        >
                                            <Chip key={reportData?.Report?.PushbackEntry.Status} label={reportData?.Report?.PushbackEntry.Status} sx={{ backgroundColor: getPushbackStatusColor(reportData?.Report?.PushbackEntry.Status), color: 'white' }} />
                                        </Typography>
                                        <Typography variant="body1" color="textSecondary"><strong>Last Status Change:</strong></Typography>
                                        <Typography variant="body1">{dayjs(reportData?.Report?.PushbackEntry.LastStatusChangeTimestamp).format('MM/DD/YYYY hh:mm:ss A Z')}</Typography>
                                    </Box>
                                </Box>
                            </Box>

                            <Divider sx={{ my: 1 }} />

                            {/* Tabs */}
                            <Box sx={{ width: '100%' }}>
                                <Tabs value={tabValue} onChange={handleTabChange}>
                                    <Tab label="Process History" {...docPanelProps(0)} />
                                    <Tab label="Client Logs" {...docPanelProps(1)} />
                                    <Tab
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                PushBack
                                                <Tooltip title="Copy to clipboard">
                                                    <IconButton
                                                        onClick={(e) => {
                                                            navigator.clipboard.writeText(reportData?.Report?.PushbackEventXml || '');
                                                        }}
                                                        size="small"
                                                        sx={{ ml: 1 }}
                                                    >
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        }
                                        {...docPanelProps(2)}
                                    />
                                </Tabs>
                                <DocsTabPanel value={tabValue} index={0}>
                                    <Box sx={{ width: '100%', height: 300 }}>
                                        <DtDataGrid
                                            data={reportData?.Report.PushbackHistoryEntries || []}
                                            columns={pushBackDetailColumnReport()}
                                            tableName="pushBackDetails"
                                        />
                                    </Box>
                                </DocsTabPanel>
                                <DocsTabPanel value={tabValue} index={1}>
                                    <Box sx={{ width: '100%', height: 300 }}>
                                        <DtDataGrid
                                            data={reportData?.Report.ClientLogEntries || []}
                                            columns={clientLogs()}
                                            tableName="logs"
                                            containerSx={{ height: '100%', width: '100%', overflow: 'auto' }}
                                        />
                                    </Box>
                                </DocsTabPanel>
                                <DocsTabPanel value={tabValue} index={2}>
                                    <Box sx={{ border: '1px solid grey', padding: 1, borderRadius: 2 }}>
                                        <Box
                                            sx={{
                                                width: "100%",
                                                height: '35vh',
                                                overflowY: 'auto',
                                                overflowX: 'scroll',
                                                whiteSpace: 'pre-wrap',
                                                wordWrap: 'break-word',
                                                wordBreak: 'break-word',
                                                padding: '0.1rem',
                                            }}
                                        >
                                            <pre>{reportData?.Report?.PushbackEventXml}</pre>
                                        </Box>
                                    </Box>
                                </DocsTabPanel>
                            </Box>
                        </Box>
                    </DialogContent>
                </>
            )}
        </DtDialog>
    );
}
interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function DocsTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`bundle-tabpanel-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 1, height: 1 }}>{children}</Box>}
        </div>
    );
}

function docPanelProps(index: number) {
    return {
        id: `bundle-tab-${index}`,
    };
}

export default PushbackReportingDialog;